import { useEffect, useMemo, useState } from 'react';
import type { DialectTypeCatalog, DialectTypeDescriptor, TypeCategory, TypeValidationResult } from '@bits2schema/shared';
import { buildTypeIndex, formatType, parseTypeString, validateTypeWithCatalog } from '@bits2schema/shared';
import { useT, type TranslateFn } from '../i18n';

const RAW_OPTION = '__raw__';

export interface ColumnTypeValue {
  rawType: string;
  canonicalType: string | null;
  isTypeRecognized: boolean;
}

interface ColumnTypeEditorProps {
  catalog: DialectTypeCatalog;
  value: ColumnTypeValue;
  onCommit: (value: ColumnTypeValue) => void;
}

export function describeTypeValidation(t: TranslateFn, result: TypeValidationResult): string {
  switch (result.reason) {
    case 'empty':
      return t('schema.type.reason.empty');
    case 'unknownType':
      return t('schema.type.reason.unknownType', { detail: result.detail ?? '' });
    case 'parameterRequired':
      return t('schema.type.reason.parameterRequired', { detail: result.detail ?? '' });
    case 'parameterNotAllowed':
      return t('schema.type.reason.parameterNotAllowed', { detail: result.detail ?? '' });
    case 'parameterInvalid':
      return t('schema.type.reason.parameterInvalid', { detail: result.detail ?? '' });
    default:
      return '';
  }
}

/**
 * Editor typu sloupce (E4-US4/US9) — dropdown se známými typy dialektu
 * (`getTypeCatalog()`, Epic 10) + podmíněné parametrové pole(a) dle
 * `TypeParameterKind`. Validace běží klientsky přes stejnou sdílenou funkci
 * jako backend (`validateTypeWithCatalog`, `packages/shared/src/typeCatalog.ts`)
 * — pravidlo #1 command vrstvy: validace patří do UI PŘED vytvořením
 * commandu. "Vlastní / neznámý typ" je únikový poklop pro ruční opravu
 * nerozpoznaného importovaného typu (E1-US5) i pro typy mimo kurátorovaný
 * katalog (např. DuckDB `STRUCT(...)`).
 */
export function ColumnTypeEditor({ catalog, value, onCommit }: ColumnTypeEditorProps) {
  const t = useT();
  const index = useMemo(() => buildTypeIndex(catalog), [catalog]);
  const parsed = useMemo(() => parseTypeString(value.rawType), [value.rawType]);
  const initialDescriptor = parsed ? index.get(parsed.baseName) : undefined;

  const [selected, setSelected] = useState(initialDescriptor ? initialDescriptor.name : RAW_OPTION);
  const [params, setParams] = useState<string[]>(parsed?.params.map(String) ?? []);
  const [rawText, setRawText] = useState(value.rawType);

  useEffect(() => {
    setSelected(initialDescriptor ? initialDescriptor.name : RAW_OPTION);
    setParams(parsed?.params.map(String) ?? []);
    setRawText(value.rawType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.rawType]);

  const descriptor = selected === RAW_OPTION ? undefined : index.get(selected.toLowerCase());
  const arrayDepth = parsed?.arrayDepth ?? 0;

  const composed =
    selected === RAW_OPTION
      ? rawText
      : formatType(
          descriptor?.name ?? selected,
          descriptor?.parameters === 'none' ? [] : params.filter((p) => p.trim() !== '').map(Number),
          arrayDepth,
        );
  const validation = validateTypeWithCatalog(index, composed);

  const commit = (nextRawType: string) => {
    const result = validateTypeWithCatalog(index, nextRawType);
    if (!result.valid) return;
    const nextParsed = parseTypeString(nextRawType);
    const nextDescriptor = nextParsed ? index.get(nextParsed.baseName) : undefined;
    onCommit({
      rawType: nextRawType,
      canonicalType: nextDescriptor
        ? formatType(nextDescriptor.name, nextParsed!.params, nextParsed!.arrayDepth)
        : result.valid
          ? nextRawType
          : null,
      isTypeRecognized: Boolean(nextDescriptor) || result.valid,
    });
  };

  const categories = useMemo(() => {
    const byCategory = new Map<TypeCategory, DialectTypeDescriptor[]>();
    for (const type of catalog.types) {
      const list = byCategory.get(type.category) ?? [];
      list.push(type);
      byCategory.set(type.category, list);
    }
    return byCategory;
  }, [catalog]);

  return (
    <div className="field">
      <label htmlFor="inspector-column-type">{t('inspector.column.type')}</label>
      <select
        id="inspector-column-type"
        value={selected}
        onChange={(event) => {
          const nextName = event.target.value;
          setSelected(nextName);
          if (nextName === RAW_OPTION) return;
          const nextDescriptor = index.get(nextName.toLowerCase());
          const nextParams = nextDescriptor?.parameters === 'none' ? [] : [];
          setParams(nextParams);
          commit(formatType(nextDescriptor?.name ?? nextName, [], 0));
        }}
      >
        {[...categories.entries()].map(([category, types]) => (
          <optgroup key={category} label={t(`schema.type.category.${category}`)}>
            {types.map((type) => (
              <option key={type.name} value={type.name}>
                {type.name}
              </option>
            ))}
          </optgroup>
        ))}
        <option value={RAW_OPTION}>{t('inspector.column.typeCustom')}</option>
      </select>

      {selected === RAW_OPTION && (
        <input
          value={rawText}
          onChange={(event) => setRawText(event.target.value)}
          onBlur={(event) => commit(event.target.value)}
          placeholder={t('inspector.column.typeCustomPlaceholder')}
        />
      )}

      {descriptor?.parameters === 'length' && (
        <div className="field">
          <label htmlFor="inspector-column-type-length">{t('inspector.column.typeParamLength')}</label>
          <input
            id="inspector-column-type-length"
            type="number"
            min={1}
            value={params[0] ?? ''}
            onChange={(event) => setParams([event.target.value])}
            onBlur={() => commit(composed)}
          />
        </div>
      )}

      {descriptor?.parameters === 'precisionScale' && (
        <div className="inline-fields">
          <div className="field">
            <label htmlFor="inspector-column-type-precision">{t('inspector.column.typeParamPrecision')}</label>
            <input
              id="inspector-column-type-precision"
              type="number"
              min={1}
              value={params[0] ?? ''}
              onChange={(event) => setParams([event.target.value, params[1] ?? ''])}
              onBlur={() => commit(composed)}
            />
          </div>
          <div className="field">
            <label htmlFor="inspector-column-type-scale">{t('inspector.column.typeParamScale')}</label>
            <input
              id="inspector-column-type-scale"
              type="number"
              min={0}
              value={params[1] ?? ''}
              onChange={(event) => setParams([params[0] ?? '', event.target.value])}
              onBlur={() => commit(composed)}
            />
          </div>
        </div>
      )}

      {!validation.valid && <p className="notice notice--error">{describeTypeValidation(t, validation)}</p>}
    </div>
  );
}
